import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './PaymentPorterPage.css';
import './ProfileIATAPage.css';
import pmmService from '../services/paymentMigrationManagerService';
import { formatDate, formatDateTime } from '../utils/datetime';

const fmtDate = (v) => formatDate(v);

const statusBadge = (active) => (
    <span className={`pp-status-badge ${active ? 'status-active' : 'status-inactive'}`}>
        {active ? 'Active' : 'Inactive'}
    </span>
);

const PaymentMigrationManagerPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [pmm, setPmm] = useState(null);
    const [tab, setTab] = useState('criteria');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [form, setForm] = useState({});
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await pmmService.getById(id);
            const data = res.data?.pmm;
            setPmm(data);
            setForm({
                name:                  data.name               || '',
                description:           data.description        || '',
                marketplace_id:        data.marketplace_id     || '',
                hotelid_override:      data.hotelid_override   || '',
                window_number:         data.window_number != null ? data.window_number : '',
                udf_field:             data.udf_field          || 'UDFC01',
                active:                data.active !== false,
            });
        } catch (err) {
            console.error('Failed to load PMM', err);
            setError('Failed to load Payment Migration Manager');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await pmmService.update(id, {
                ...pmm,
                name:             form.name,
                description:      form.description,
                marketplace_id:   form.marketplace_id || null,
                hotelid_override: form.hotelid_override || null,
                window_number:    form.window_number !== '' ? parseInt(form.window_number) : null,
                udf_field:        form.udf_field || 'UDFC01',
                active:           form.active,
            });
            setEditMode(false);
            load();
        } catch (err) {
            console.error('Save failed', err);
            setError('Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCriteria = async (type, itemId) => {
        if (!window.confirm('Remove this item?')) return;
        try {
            if (type === 'corp')    await pmmService.deleteCorporateProfile(id, itemId);
            if (type === 'iata')    await pmmService.deleteIataProfile(id, itemId);
            if (type === 'rate')    await pmmService.deleteRateCode(id, itemId);
            if (type === 'method')  await pmmService.deletePaymentMethod(id, itemId);
            if (type === 'assign')  await pmmService.deleteProfileAssignment(id, itemId);
            if (type === 'routing') await pmmService.deleteRouting(id, itemId);
            if (type === 'rcode')   await pmmService.deleteRoutingCode(id, itemId);
            load();
        } catch (err) {
            console.error('Delete failed', err);
        }
    };

    if (loading) return <div className="pi-page"><div className="pi-empty-title" style={{padding:40}}>Loading…</div></div>;
    if (error)   return <div className="pi-page"><div className="pi-empty-title" style={{padding:40,color:'#dc2626'}}>{error}</div></div>;
    if (!pmm)    return null;

    const tabs = [
        { key: 'criteria', label: 'Criteria' },
        { key: 'actions',  label: 'Actions'  },
        { key: 'settings', label: 'Settings' },
        { key: 'runs',     label: 'Run History' },
    ];

    return (
        <div className="pi-page">
            <div className="pi-header">
                <div className="pi-header-left">
                    <button className="pi-back-btn" onClick={() => navigate('/apps/payment-migration-manager')}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Payment Migration Manager
                    </button>
                    <div className="pi-title-block">
                        <h1 className="pi-title">{pmm.name}</h1>
                        <p className="pi-subtitle">{pmm.description || 'No description'}</p>
                    </div>
                </div>
                <div className="pi-header-actions">
                    {statusBadge(pmm.active)}
                    <button className="pi-btn-add" onClick={() => navigate(`/apps/payment-migration-manager/${id}/run`)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M5 3l8 5-8 5V3z" fill="currentColor"/>
                        </svg>
                        Run
                    </button>
                </div>
            </div>

            <div className="pp-tabs">
                {tabs.map(t => (
                    <button key={t.key} className={`pp-tab ${tab === t.key ? 'pp-tab--active' : ''}`} onClick={() => setTab(t.key)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'criteria' && (
                <div className="pi-list-panel" style={{padding: 20}}>
                    <CriteriaSection title="Corporate Profiles" items={pmm.corporateProfiles || []}
                        columns={['Agent Type', 'Profile ID', 'Name', 'Country']}
                        renderRow={p => [p.agent_type, p.profile_id, p.name, p.country || '—']}
                        onAdd={() => navigate(`/apps/payment-migration-manager/${id}/corporate-profiles/add`)}
                        onDelete={p => handleDeleteCriteria('corp', p.id)}
                    />
                    <CriteriaSection title="IATA Profiles" items={pmm.iataProfiles || []}
                        columns={['Type', 'IATA ID', 'Name', 'Country']}
                        renderRow={p => [p.profile_type, p.corp_id_iata, p.name, p.country || '—']}
                        onAdd={() => navigate(`/apps/payment-migration-manager/${id}/iata-profiles/add`)}
                        onDelete={p => handleDeleteCriteria('iata', p.id)}
                    />
                    <CriteriaSection title="Rate Codes" items={pmm.rateCodes || []}
                        columns={['Rate Code', 'Name']}
                        renderRow={r => [r.rate_code, r.rate_code_name]}
                        onAdd={() => navigate(`/apps/payment-migration-manager/${id}/rate-codes/add`)}
                        onDelete={r => handleDeleteCriteria('rate', r.id)}
                    />
                    <CriteriaSection title="Payment Methods" items={pmm.paymentMethods || []}
                        columns={['Code', 'Description']}
                        renderRow={m => [m.code, m.description || '—']}
                        onAdd={() => navigate(`/apps/payment-migration-manager/${id}/payment-methods/add`)}
                        onDelete={m => handleDeleteCriteria('method', m.id)}
                    />
                </div>
            )}

            {tab === 'actions' && (
                <div className="pi-list-panel" style={{padding: 20}}>
                    <CriteriaSection title="Profile Assignments" items={pmm.profileAssignments || []}
                        columns={['Profile ID', 'Name', 'Type']}
                        renderRow={a => [a.profile_id, a.profile_name || '—', a.profile_type || '—']}
                        onAdd={() => navigate(`/apps/payment-migration-manager/${id}/profile-assignments/add`)}
                        onDelete={a => handleDeleteCriteria('assign', a.id)}
                    />
                    <CriteriaSection title="Routing" items={pmm.routing || []}
                        columns={['Code', 'Type', 'Description', 'Window']}
                        renderRow={r => [r.code, r.type || '—', r.description || '—', r.default_window || '—']}
                        onAdd={() => navigate(`/apps/payment-migration-manager/${id}/routing/add`)}
                        onDelete={r => handleDeleteCriteria('routing', r.id)}
                    />
                    <CriteriaSection title="Routing Code Library" items={pmm.routingCodes || []}
                        columns={['Code', 'Type', 'Description', 'Default Window']}
                        renderRow={r => [r.code, r.routing_type || '—', r.description || '—', r.default_window || '—']}
                        onAdd={() => navigate(`/apps/payment-migration-manager/${id}/routing-codes/add`)}
                        onDelete={r => handleDeleteCriteria('rcode', r.id)}
                    />
                </div>
            )}

            {tab === 'settings' && (
                <div className="pi-list-panel" style={{padding: 20}}>
                    {editMode ? (
                        <div style={{maxWidth: 480}}>
                            <div className="pi-form-group">
                                <label className="pi-form-label">Name</label>
                                <input className="pi-form-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} />
                            </div>
                            <div className="pi-form-group">
                                <label className="pi-form-label">Description</label>
                                <textarea className="pi-form-input" rows={3} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} />
                            </div>
                            <div className="pi-form-group">
                                <label className="pi-form-label">UDF Field</label>
                                <input className="pi-form-input" value={form.udf_field} onChange={e => setForm(f => ({...f, udf_field: e.target.value}))} />
                            </div>
                            <div className="pi-form-group">
                                <label className="pi-form-label">Folio Window</label>
                                <input className="pi-form-input" type="number" value={form.window_number} onChange={e => setForm(f => ({...f, window_number: e.target.value}))} />
                            </div>
                            <div className="pi-form-group">
                                <label className="pi-form-label">Hotel ID Override</label>
                                <input className="pi-form-input" value={form.hotelid_override} onChange={e => setForm(f => ({...f, hotelid_override: e.target.value}))} />
                            </div>
                            <div className="pi-form-group">
                                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                                    <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({...f, active: e.target.checked}))} />
                                    Active
                                </label>
                            </div>
                            <div style={{display:'flex',gap:8,marginTop:16}}>
                                <button className="pi-btn-add" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                                <button className="pp-refresh-btn" onClick={() => setEditMode(false)}>Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <table className="pi-table" style={{maxWidth: 480}}>
                                <tbody>
                                    <tr><td><strong>Name</strong></td><td>{pmm.name}</td></tr>
                                    <tr><td><strong>Description</strong></td><td>{pmm.description || '—'}</td></tr>
                                    <tr><td><strong>Hotel</strong></td><td>{pmm.hotel_name || '—'}</td></tr>
                                    <tr><td><strong>Account</strong></td><td>{pmm.account_name || '—'}</td></tr>
                                    <tr><td><strong>Marketplace ID</strong></td><td>{pmm.marketplace_id || '—'}</td></tr>
                                    <tr><td><strong>UDF Field</strong></td><td>{pmm.udf_field || 'UDFC01'}</td></tr>
                                    <tr><td><strong>Folio Window</strong></td><td>{pmm.window_number != null ? pmm.window_number : '—'}</td></tr>
                                    <tr><td><strong>Hotel ID Override</strong></td><td>{pmm.hotelid_override || '—'}</td></tr>
                                    <tr><td><strong>Status</strong></td><td>{statusBadge(pmm.active)}</td></tr>
                                    <tr><td><strong>Created</strong></td><td>{fmtDate(pmm.created_at)}</td></tr>
                                </tbody>
                            </table>
                            <button className="pi-btn-add" style={{marginTop: 16}} onClick={() => setEditMode(true)}>Edit Settings</button>
                        </div>
                    )}
                </div>
            )}

            {tab === 'runs' && <RunHistoryTab pmmId={id} navigate={navigate} />}
        </div>
    );
};

const CriteriaSection = ({ title, items, columns, renderRow, onAdd, onDelete }) => (
    <div style={{marginBottom: 32}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 12}}>
            <h3 style={{margin:0,fontSize:15,fontWeight:600}}>{title}</h3>
            <button className="pi-btn-add" style={{padding:'4px 12px', fontSize:13}} onClick={onAdd}>+ Add</button>
        </div>
        {items.length === 0 ? (
            <p style={{color:'#9ca3af',fontSize:13}}>No {title.toLowerCase()} configured.</p>
        ) : (
            <table className="pi-table">
                <thead><tr>{columns.map(c => <th key={c}>{c}</th>)}<th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                    {items.map(item => (
                        <tr key={item.id}>
                            {renderRow(item).map((v, i) => <td key={i}>{v}</td>)}
                            <td><span className={`pp-status-badge ${item.active ? 'status-active' : 'status-inactive'}`}>{item.active ? 'Active' : 'Inactive'}</span></td>
                            <td>
                                <button className="pi-action-btn pi-action-btn--danger" onClick={() => onDelete(item)}>Remove</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
);

const RunHistoryTab = ({ pmmId, navigate }) => {
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        pmmService.listRuns(pmmId).then(res => {
            setRuns(res.data?.runs || []);
        }).catch(console.error).finally(() => setLoading(false));
    }, [pmmId]);

    if (loading) return <div style={{padding:20}}>Loading…</div>;

    return (
        <div className="pi-list-panel" style={{padding: 20}}>
            {runs.length === 0 ? (
                <div className="pi-empty-title">No runs yet. Use the Run button to process reservations.</div>
            ) : (
                <table className="pi-table">
                    <thead>
                        <tr>
                            <th>Run Reference</th>
                            <th>Date Range</th>
                            <th>Total</th>
                            <th>Success</th>
                            <th>Errors</th>
                            <th>Skipped</th>
                            <th>Started</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {runs.map(r => (
                            <tr key={r.run_ref}>
                                <td style={{fontFamily:'monospace', fontSize:12}}>{r.run_ref}</td>
                                <td>{r.start_date} → {r.end_date}<br/><span style={{color:'#6b7280',fontSize:12}}>{r.date_type}</span></td>
                                <td>{r.total}</td>
                                <td style={{color: r.success_count > 0 ? '#16a34a' : undefined}}>{r.success_count}</td>
                                <td style={{color: r.error_count > 0 ? '#dc2626' : undefined}}>{r.error_count}</td>
                                <td>{r.skipped_count}</td>
                                <td style={{fontSize:12}}>{formatDateTime(r.started_at)}</td>
                                <td>
                                    <button className="pi-action-btn" onClick={() => navigate(`/apps/payment-migration-manager/${pmmId}/runs/${encodeURIComponent(r.run_ref)}`)}>
                                        View
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default PaymentMigrationManagerPage;
